/**
 * 제약사 등록·수정 공통 폼.
 * admin-ruda-web `pharmaceuticalCompanyInputs.js` 필드 구성을 맞춘다.
 */
import {
  useCreatePharmaCompany,
  usePharmaCompanyDetail,
  useUpdatePharmaCompany,
} from '@/entities/pharma/hooks/usePharmaCompanies';
import { useMenuAccess } from '@/layouts/MenuAccessContext';
import { AddressSearchField } from '@/shared/components/AddressSearchField';
import { FormDateRangeField } from '@/shared/components/FormDateField';
import { FormSelectField } from '@/shared/components/FormSelectField';
import { FormTextField } from '@/shared/components/FormTextField';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { FooterRow, Stack, ToolbarRow } from '@/shared/components/ui/row';
import { Textarea } from '@/shared/components/ui/textarea';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  defaultPharmaFormValues,
  detailToFormValues,
  FILTER_INFO_OPTIONS,
  getContractStatusLabel,
  MONTH_DAY_OPTIONS,
  ORIGINAL_SUBMISSION_OPTIONS,
  SETTLEMENT_COMPLETED_DAY_OPTIONS,
  TAX_INVOICE_METHOD_OPTIONS,
  toCreatePayload,
  type PharmaCompanyFormValues,
} from '../pharmaFormUtils';

const pharmaFormSchema = z.object({
  pharmaCompanyId: z.string(),
  pharmaCompanyName: z.string().min(1, '제약사명을 입력해주세요'),
  businessLicenseNumber: z.string().min(1, '사업자등록번호를 입력해주세요'),
  representativeName: z.string().min(1, '대표자명을 입력해주세요'),
  mainManagerPhone: z.string().min(1, '대표 전화번호를 입력해주세요'),
  contractStartDate: z.string().min(1, '계약 시작일자를 입력해주세요'),
  contractEndDate: z.string().min(1, '계약 종료일자를 입력해주세요'),
  filterInfo: z.string().min(1, '필터링 입력 정보를 선택해주세요'),
  contractStatus: z.string(),
  minPrescriptionAmount: z.string().min(1, '최소처방금액을 입력해주세요'),
  address: z.string(),
  mainManagerName: z.string(),
  mainManagerEmail: z.string(),
  mainManagerFaxNumber: z.string(),
  hospitalManagerName: z.string(),
  hospitalManagerPhone: z.string(),
  hospitalManagerEmail: z.string(),
  hospitalManagerKakao: z.string(),
  inventoryManagerName: z.string(),
  inventoryManagerPhone: z.string(),
  inventoryManagerEmail: z.string(),
  inventoryManagerKakao: z.string(),
  settlementManagerName: z.string(),
  settlementManagerPhone: z.string(),
  settlementManagerEmail: z.string(),
  settlementManagerKakao: z.string(),
  taxInvoiceManagerName: z.string(),
  taxInvoiceManagerPhone: z.string(),
  taxInvoiceManagerEmail: z.string(),
  taxInvoiceManagerKakao: z.string(),
  prescriptionDataRegisteredAt: z.string(),
  settlementCompletedAt: z.string(),
  taxInvoiceIssueStandardDate: z.string(),
  taxInvoiceIssueMethod: z.string(),
  commissionPaymentDate: z.string(),
  commissionVerificationMethod: z.string(),
  expenseReportBaseDate: z.string(),
  settlementDataEntry: z.string(),
  originalSubmission: z.enum(['', 'true', 'false']),
  originalSubmissionMethod: z.string(),
  remarks: z.string(),
});

type Props =
  | { mode: 'create' }
  | { mode: 'edit'; pharmaCompanyId: number };

export const PharmaCompanyForm: React.FC<Props> = (props) => {
  const isCreate = props.mode === 'create';
  const pharmaCompanyId = props.mode === 'edit' ? props.pharmaCompanyId : null;

  const toast = useToast();
  const navigate = useNavigate();
  const { readOnly } = useMenuAccess();
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [quitDialogOpen, setQuitDialogOpen] = useState(false);

  const { data, isLoading, isError, error } = usePharmaCompanyDetail(
    isCreate ? null : pharmaCompanyId,
  );
  const createMutation = useCreatePharmaCompany();
  const updateMutation = useUpdatePharmaCompany();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const form = useForm<PharmaCompanyFormValues>({
    resolver: zodResolver(pharmaFormSchema),
    defaultValues: defaultPharmaFormValues,
  });

  const contractStartDate = form.watch('contractStartDate');
  const contractEndDate = form.watch('contractEndDate');
  const computedContractStatus = getContractStatusLabel(
    contractStartDate,
    contractEndDate,
  );

  useEffect(() => {
    if (data && !isCreate) {
      form.reset(detailToFormValues(data));
    }
  }, [data, form, isCreate]);

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  const goBack = () =>
    navigate({ to: ROUTE_PATHS.PHARMA_COMPANIES } as never);

  const handleBack = () => {
    if (form.formState.isDirty) {
      setQuitDialogOpen(true);
      return;
    }
    goBack();
  };

  const handleSave = async (values: PharmaCompanyFormValues) => {
    // contractStatus: 표시는 시작·종료일 기준 라벨, 전송은 레거시와 동일하게 폼에 로드된 Y/N 유지
    const payload = toCreatePayload(values);

    try {
      if (isCreate) {
        await createMutation.mutateAsync(payload);
        toast.success('제약사가 등록되었습니다.');
        goBack();
      } else if (pharmaCompanyId != null) {
        await updateMutation.mutateAsync({
          pharmaCompanyId,
          data: payload,
        });
        toast.success('제약사 정보가 저장되었습니다.');
        form.reset(values);
      }
      setSaveDialogOpen(false);
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  if (!isCreate && isLoading) {
    return (
      <div className="py-16">
        <LoadingSpinner tip="제약사 정보를 불러오는 중..." />
      </div>
    );
  }

  if (!isCreate && !data) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        제약사 정보를 찾을 수 없습니다.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ToolbarRow>
        <h2 className="text-2xl font-semibold">
          {isCreate ? '제약사 등록' : '제약사 상세'}
        </h2>
      </ToolbarRow>

      <Form {...form}>
        <form
          className="space-y-6"
          onSubmit={form.handleSubmit(() => setSaveDialogOpen(true))}
        >
          <Card>
            <CardHeader>
              <CardTitle>기본 정보</CardTitle>
            </CardHeader>
            <CardContent>
              <Stack gap={16}>
                {!isCreate ? (
                  <div className="grid gap-4 lg:grid-cols-4">
                    <FormTextField
                      control={form.control}
                      name="pharmaCompanyId"
                      label="제약사 고유번호"
                      disabled
                    />
                  </div>
                ) : null}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <FormTextField
                    control={form.control}
                    name="pharmaCompanyName"
                    label="제약사명"
                    required
                    disabled={readOnly}
                  />
                  <FormItem>
                    <FormLabel>계약 상태</FormLabel>
                    <Input disabled value={computedContractStatus} />
                  </FormItem>
                  <FormDateRangeField
                    control={form.control}
                    fromName="contractStartDate"
                    toName="contractEndDate"
                    label="계약 기간"
                    required
                    placeholder="계약 시작일 ~ 종료일"
                    className="md:col-span-2"
                    disabled={readOnly}
                  />
                </div>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>사업자 정보</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <FormTextField
                  control={form.control}
                  name="businessLicenseNumber"
                  label="사업자등록번호"
                  required
                  disabled={readOnly}
                />
                <FormTextField
                  control={form.control}
                  name="representativeName"
                  label="대표자명"
                  required
                  disabled={readOnly}
                />
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2 lg:col-span-2">
                      <FormLabel>주소</FormLabel>
                      <AddressSearchField
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        disabled={readOnly}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <ManagerCard
            title="대표 담당자"
            prefix="mainManager"
            form={form}
            phoneRequired
            disabled={readOnly}
          />
          <ManagerCard
            title="병원 신규 담당자"
            prefix="hospitalManager"
            form={form}
            disabled={readOnly}
          />
          <ManagerCard
            title="재고 유통 담당자"
            prefix="inventoryManager"
            form={form}
            disabled={readOnly}
          />
          <ManagerCard
            title="정산 담당자"
            prefix="settlementManager"
            form={form}
            disabled={readOnly}
          />
          <ManagerCard
            title="세금계산서 담당자"
            prefix="taxInvoiceManager"
            form={form}
            disabled={readOnly}
          />

          <Card>
            <CardHeader>
              <CardTitle>정산·세금계산서 정보</CardTitle>
            </CardHeader>
            <CardContent>
              <Stack gap={16}>
                {/* Row1: 처방자료 등록일 | 최소처방금액 | 필터링 입력 정보 */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <FormSelectField
                    control={form.control}
                    name="prescriptionDataRegisteredAt"
                    label="처방자료 등록일(매월)"
                    options={[...MONTH_DAY_OPTIONS]}
                    disabled={readOnly}
                  />
                  <FormTextField
                    control={form.control}
                    name="minPrescriptionAmount"
                    label="최소처방금액"
                    type="number"
                    required
                    disabled={readOnly}
                  />
                  <FormSelectField
                    control={form.control}
                    name="filterInfo"
                    label="필터링 입력 정보"
                    required
                    options={[...FILTER_INFO_OPTIONS]}
                    disabled={readOnly}
                  />
                </div>

                {/* Row2: 세금계산서 작성 기준일 | 발행 방법 */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <FormSelectField
                    control={form.control}
                    name="taxInvoiceIssueStandardDate"
                    label="세금계산서 작성 기준일 (매월)"
                    options={[...MONTH_DAY_OPTIONS]}
                    disabled={readOnly}
                  />
                  <FormSelectField
                    control={form.control}
                    name="taxInvoiceIssueMethod"
                    label="세금계산서 발행 방법"
                    options={[...TAX_INVOICE_METHOD_OPTIONS]}
                    disabled={readOnly}
                  />
                </div>

                {/* Row3: 수수료 지급일 | 수수료 확인 방법 */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <FormSelectField
                    control={form.control}
                    name="commissionPaymentDate"
                    label="수수료 지급일(매월)"
                    options={[...MONTH_DAY_OPTIONS]}
                    disabled={readOnly}
                  />
                  <FormTextField
                    control={form.control}
                    name="commissionVerificationMethod"
                    label="수수료 확인 방법"
                    disabled={readOnly}
                  />
                </div>

                {/* Row4: 정산자료 입력처 | 정산 완료일 | 지급보고서 작성 기준일 */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <FormTextField
                    control={form.control}
                    name="settlementDataEntry"
                    label="정산자료 입력처"
                    disabled={readOnly}
                  />
                  <FormSelectField
                    control={form.control}
                    name="settlementCompletedAt"
                    label="정산 완료일(매월)"
                    options={[...SETTLEMENT_COMPLETED_DAY_OPTIONS]}
                    disabled={readOnly}
                  />
                  <FormSelectField
                    control={form.control}
                    name="expenseReportBaseDate"
                    label="지급보고서 작성 기준일(매월)"
                    options={[...MONTH_DAY_OPTIONS]}
                    disabled={readOnly}
                  />
                </div>

                {/* Row5: 원본 제출 여부(originalSubmission) | 원본 제출 방식 */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <FormSelectField
                    control={form.control}
                    name="originalSubmission"
                    label="원본 제출 여부"
                    options={[...ORIGINAL_SUBMISSION_OPTIONS]}
                    disabled={readOnly}
                  />
                  <FormTextField
                    control={form.control}
                    name="originalSubmissionMethod"
                    label="원본 제출 방식"
                    disabled={readOnly}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="remarks"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>비고</FormLabel>
                      <Textarea
                        {...field}
                        rows={4}
                        placeholder="비고를 입력해 주세요"
                        disabled={readOnly}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </Stack>
            </CardContent>
          </Card>

          <FooterRow>
            <Button type="button" variant="outline" onClick={handleBack}>
              나가기
            </Button>
            <MenuWriteGate>
              <Button
                type="submit"
                disabled={(!isCreate && !form.formState.isDirty) || isPending}
              >
                {isCreate ? '등록' : '저장'}
              </Button>
            </MenuWriteGate>
          </FooterRow>
        </form>
      </Form>

      <AlertDialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isCreate ? '등록 확인' : '저장 확인'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isCreate
                ? '입력한 내용으로 제약사를 등록합니다.'
                : '변경된 내용으로 저장됩니다.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={form.handleSubmit(handleSave)}
              disabled={isPending}
            >
              확인
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={quitDialogOpen} onOpenChange={setQuitDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>나가기</AlertDialogTitle>
            <AlertDialogDescription>
              저장하지 않은 변경 사항이 있습니다. 목록으로 돌아가시겠습니까?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>계속 편집</AlertDialogCancel>
            <AlertDialogAction onClick={goBack}>나가기</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

type FormType = ReturnType<typeof useForm<PharmaCompanyFormValues>>;

const ManagerCard = ({
  title,
  prefix,
  form,
  phoneRequired,
  disabled,
}: {
  title: string;
  prefix:
    | 'mainManager'
    | 'hospitalManager'
    | 'inventoryManager'
    | 'settlementManager'
    | 'taxInvoiceManager';
  form: FormType;
  phoneRequired?: boolean;
  disabled?: boolean;
}) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-base">{title}</CardTitle>
    </CardHeader>
    <CardContent className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <FormTextField
        control={form.control}
        name={`${prefix}Name` as keyof PharmaCompanyFormValues}
        label="이름"
        disabled={disabled}
      />
      <FormTextField
        control={form.control}
        name={`${prefix}Phone` as keyof PharmaCompanyFormValues}
        label="연락처"
        required={phoneRequired}
        disabled={disabled}
      />
      <FormTextField
        control={form.control}
        name={`${prefix}Email` as keyof PharmaCompanyFormValues}
        label="이메일"
        disabled={disabled}
      />
      {prefix !== 'mainManager' ? (
        <FormTextField
          control={form.control}
          name={`${prefix}Kakao` as keyof PharmaCompanyFormValues}
          label="카카오톡"
          disabled={disabled}
        />
      ) : (
        <FormTextField
          control={form.control}
          name="mainManagerFaxNumber"
          label="팩스"
          disabled={disabled}
        />
      )}
    </CardContent>
  </Card>
);
